import {Field, Form, FormValidation, resetSubmitting, getFormData, untouch} from '@nexios/frontend-forms';
import formatPhone from '@nexios/phone-number-formatter';
import {isSepaCountryIban} from '@nexios/validation';
import BigNumber from 'bignumber.js';
import parse from 'html-react-parser';
import IBAN from 'iban';
import _ from 'lodash';
import React from 'react';
import {Helmet} from 'react-helmet';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import styled, {withTheme} from 'styled-components';
import Box from '../../components/Box';
import BottomImage from '../../components/BottomImage';
import Button from '../../components/Button';
import ButtonContainer from '../../components/ButtonContainer';
import DateInput from '../../components/DateInput';
import ErrorMessage from '../../components/ErrorMessage';
import FieldWrapper from '../../components/FieldWrapper';
import Input from '../../components/Input';
import InputAmount from '../../components/InputAmount';
import Label from '../../components/Label';
import SubLabel from '../../components/SubLabel';
import LayoutContainer from '../../components/LayoutContainer';
import Logo from '../../components/Logo';
import MainContainer from '../../components/MainContainer';
import ModalDialog from '../../components/ModalDialog';
import ProgressBar from '../../components/ProgressBar';
import RadioButtonContainer from '../../components/RadioButtonContainer';
import Teaser from '../../components/Teaser';
import Telemarketing from '../../components/Telemarketing';
import NewsletterOptIn from '../../components/NewsletterOptIn';
import {optInChoices, normalizeOptIns} from '../../utils/optIn';
import Title from '../../components/Title';
import Validation from '../../components/Validation';
import {gaMembership1} from '../../googleAnalytics/membership1';
import useScrollToTop from '../../hooks/useScrollToTop';
import {cleanseAddress, closeDialog, renderDialog, saveMembership} from '../../reducer';
import genders from '../../utils/genders';
import orderContactBlocks from '../../utils/orderContactBlocks';
import parseAndGetBigNumber from '../../utils/parseAndGetBigNumber';
import parseAuthorizeLabel from '../../utils/parsePrivacyStatement';
import parseText from '../../utils/parseText';
import replaceNewLinesForHtml from '../../utils/replaceNewLines';
import PrivacyLabel from '../../components/PrivacyLabel';
import TextToHtml from '../../components/TextToHtml';
import {wrap as wrapTitle} from '../../components/TitleWrapper';
import DJSBSideBar from '../../components/DJSBSideBar';
import SelectedAmountText from '../../components/SelectedAmountText';
import Dropdown from '../../components/Dropdown';
import {AddressRow, StyledAddressLabel, HouseNumberRow, FoundAddress, FoundAddressTitle, NoFoundAddress} from '../../components/Address';

const hasValue = val => !_.isNil(val) && _.trim(val) !== '';

const isAddressVisible = ctx => {
  return ctx.person.isAddressVisible;
};

const Membership1 = ({
  formContext,
  save,
  history,
  cleanse,
  gaMembership1,
  resetSubmittingForm,
  progressBar,
  renderDialog,
  closeDialog,
  isDialogVisible,
  untouchAmountFields,
  theme
}) => {
  useScrollToTop();
  let cleansing = false;
  const callCleanseAddress = (ctx, callback) => {
    const postalCode = _.get(ctx, ['data', 'postalCode']);
    const previousPostalCode = _.get(ctx, ['previousData', 'postalCode']);
    const houseNumber = _.get(ctx, ['data', 'houseNumber_i']);
    const previousHouseNumber = _.get(ctx, ['previousData', 'houseNumber_i']);
    const houseNumberAddition = _.get(ctx, ['data', 'houseNumberAddition']);
    const previousHouseNumberAddition = _.get(ctx, ['previousData', 'houseNumberAddition']);

    let isChanged = false;

    if (postalCode !== previousPostalCode || houseNumber !== previousHouseNumber || houseNumberAddition !== previousHouseNumberAddition) {
      isChanged = true;
    }

    if (hasValue(postalCode) && hasValue(houseNumber) && isChanged) {
      // if we are already cleansing do not start again, request may interfere
      if (cleansing) {
        return;
      }

      cleansing = true;

      cleanse(
        {
          postalCode,
          houseNumber_i: houseNumber,
          houseNumberAddition
        },
        data => {
          if (data.status === 500) {
            data = {
              result: {
                success: true
              },
              address: {
                street: '',
                city: ''
              }
            };
          }

          const addressCleansingStatus = _.get(data, ['result', 'success'], false) ? 'GREEN' : 'RED';

          const cleansedAddress = _.get(data, ['result', 'address']);

          if (addressCleansingStatus === 'GREEN') {
            ctx.setValues({
              street: cleansedAddress.street,
              city: cleansedAddress.city,
              postalCode: cleansedAddress.postalCode,
              houseNumber_i: cleansedAddress.houseNumber_i,
              houseNumberAddition: cleansedAddress.houseNumberAddition,
              addressCleansingStatus
            });
          } else {
            ctx.setValues({
              street: null,
              city: null,
              addressCleansingStatus
            });
          }

          ctx.validate(true);

          cleansing = false;

          return callback();
        }
      );
    } else if (!hasValue(postalCode) || !hasValue(houseNumber)) {
      ctx.setValues({
        addressCleansingStatus: null,
        street: null,
        city: null
      });
      callback();
    }
  };

  const formConfig = {
    onSubmit: () => {
      if (cleansing) {
        resetSubmittingForm();
      } else {
        gaMembership1(formContext);
        save(history);
      }
    },
    onInit: ctx => {
      ctx.setList(
        'days',
        _.range(1, 31).map(value => ({label: `${value}`, value: `${value}`}))
      );
      ctx.setList('genders', genders(formContext));
      ctx.setList('optInChoices', optInChoices);

      if (formContext.newsletterOptIn === 'dropdown') {
        const newsletterDefault = _.get(formContext, 'newsletterDefault');
        if (newsletterDefault === true || newsletterDefault === false) {
          ctx.setDefaultValues({
            allowNewsletter: newsletterDefault
          });
        }
      } else if (_.get(formContext, 'newsletterDefault', theme.allowNewsletterDefault) === true) {
        ctx.setDefaultValues({
          allowNewsletter: true
        });
      }
      if (formContext.telemarketingOptIn === 'dropdown') {
        const telemarketingDefault = _.get(formContext, 'telemarketingDefault');
        if (telemarketingDefault === true || telemarketingDefault === false) {
          ctx.setDefaultValues({
            allowTelemarketing: telemarketingDefault
          });
        }
      } else if (formContext.telemarketingOptIn !== 'none' && _.get(formContext, 'telemarketingDefault', true) === true) {
        ctx.setDefaultValues({
          allowTelemarketing: true
        });
      }

      if (formContext.amountMode === 'dynamic') {
        const amountFields = ['amountOption1_m', 'amountOption2_m', 'amountOption3_m', 'amountOption4_m'];
        const amounts = amountFields
          .filter(field => !_.isNil(formContext[field]))
          .map(field => {
            const amount = new BigNumber(formContext[field]);
            const decimalPlaces = amount.decimalPlaces() > 0 ? 2 : 0;
            let label;
            if (formContext.amountDisplay === 'amountEuroText') {
              label = `${amount.toFormat(decimalPlaces)} euro`;
            } else if (formContext.amountDisplay === 'amountNoSign') {
              label = amount.toFormat(decimalPlaces);
            } else {
              label = `€ ${amount.toFormat(decimalPlaces)}`;
            }
            return {
              value: {
                value: formContext[field]
              },
              label
            };
          });
        if (formContext.customAmountDisplay === 'button' && formContext.allowCustomAmount === true) {
          amounts.push({
            label: 'Anders',
            value: {
              value: true,
              fieldName: 'showCustomAmount'
            }
          });
        }

        ctx.setList('amounts', amounts);

        ctx.setDefaultValues({
          showCustomAmount: formContext.customAmountDisplay === 'inline' && formContext.allowCustomAmount === true
        });

        if (formContext.defaultAmountOption) {
          ctx.setDefaultValues({
            amountChoice: formContext[formContext.defaultAmountOption],
            amount_m: formContext[formContext.defaultAmountOption]
          });
        }
      } else {
        ctx.setDefaultValues({
          amount_m: formContext.amount_m
        });
      }

    },
    onDataChanged: (ctx, callback) => {
      normalizeOptIns(ctx);
      switch (ctx.field) {
        case 'postalCode':
        case 'houseNumber_i':
        case 'houseNumberAddition':
          callCleanseAddress(ctx, callback);
          break;
        case 'email': {
          ctx.setValues({
            email: _.trim(ctx.data.email)
          });
          callback();
          break;
        }
        case 'amountChoice': {
          const amountChoice = ctx.data.amountChoice;
          ctx.setValues({
            amount_m: amountChoice,
            customAmount_m: ''
          });
          if (formContext.customAmountDisplay === 'button' && hasValue(amountChoice)) {
            ctx.setValues({
              showCustomAmount: false
            });
          }
          callback();
          break;
        }
        case 'showCustomAmount': {
          if (ctx.data.showCustomAmount === true && formContext.customAmountDisplay === 'button') {
            untouchAmountFields();

            ctx.setValues({
              amountChoice: '',
              amount_m: ''
            });
          }
          if (ctx.data.showCustomAmount === false && hasValue(ctx.data.customAmount_m)) {
            ctx.setValues({
              customAmount_m: '',
              amount_m: ''
            });
          }
          callback();
          break;
        }
        case 'customAmount_m': {
          const customAmount = ctx.data.customAmount_m;
          const customAmountPrevious = ctx.previousData.customAmount_m;
          // er wordt een waarde ingevuld voor customAmount dus zet die waarde in amount en maak het keuze bedrag leeg
          // we doen dit op deze manier omdat het customAmount onder het keuze bedrag staat, als we altijd de waarde van amountChoice zouden leegmaken
          // dan zorgt het tabben door het formulier ervoor dat het keuze bedrag leeg wordt gemaakt als je in het customAmount_m veld op tab drukt
          if (hasValue(customAmount)) {
            ctx.setValues({
              amount_m: customAmount,
              amountChoice: ''
            });
          } else if (hasValue(customAmountPrevious)) {
            // we moeten echter wel voorkomen dat je het formulier kunt submitten als je eerst een customAmount_m
            // invult en daarna leegmaakt, dus maken we amount_m leeg in dit geval (zie de validatie regel op amount_m)
            ctx.setValues({amount_m: ''});
          }
          callback();
          break;
        }
        default:
          callback();
      }
    },
    fields: {
      amount_m: {
        isVisible: true,
        isRequired: false,
        rules: [
          {
            isValid: ctx => hasValue(ctx.amount_m),
            message: theme.showRequiredText && 'Bedrag is niet ingevuld'
          }
        ]
      },
      customAmount_m: {
        isVisible: ctx => ctx.allowCustomAmount === true && ctx.amountMode === 'dynamic' &&
          (formContext.customAmountDisplay === 'inline' || ctx.showCustomAmount === true),
        isRequired: false,
        rules: [
          {
            isValid: ctx => {
              if (!formContext.allowCustomAmount) {
                return true;
              }

              const customAmount = _.get(ctx, ['customAmount_m']);

              if (!hasValue(customAmount)) {
                return true;
              }

              const minAmount = BigNumber(formContext.customAmountMinimum || '1');
              const maxAmount = BigNumber(formContext.customAmountMaximum || '100000');

              const parsedCustomAmount = parseAndGetBigNumber(customAmount);

              if (_.isNil(parsedCustomAmount) || parsedCustomAmount.isNaN()) {
                return true;
              }

              return parsedCustomAmount.isGreaterThanOrEqualTo(minAmount) && parsedCustomAmount.isLessThanOrEqualTo(maxAmount);
            },
            message: () => {
              const minAmount = BigNumber(formContext.customAmountMinimum || '1');
              const maxAmount = BigNumber(formContext.customAmountMaximum || '100000');

              return `Vul een bedrag in tussen € ${minAmount.toFormat(2)} en € ${maxAmount.toFormat(2)}`;
            }
          }
        ]
      },
      amountChoice: {
        isVisible: ctx => ctx.amountMode === 'dynamic' &&
          (formContext.customAmountDisplay === 'toggle' ? ctx.showCustomAmount !== true : true),
        isRequired: false
      },
      showCustomAmount: {
        isVisible: ctx => ctx.allowCustomAmount === true && ctx.amountMode === 'dynamic',
        isRequired: false
      },
      telemarketingInfoText: {
        isVisible: ctx => {
          if (ctx.telemarketingOptIn !== 'text' && ctx.telemarketingOptIn !== 'button') {
            return false;
          }

          return ctx.person.isTelephoneVisible;
        },
        isRequired: false
      },
      allowTelemarketing: {
        isVisible: ctx => {
          if (ctx.telemarketingOptIn !== 'checkbox' && ctx.telemarketingOptIn !== 'dropdown') {
            return false;
          }

          return ctx.person.isTelephoneVisible;
        },
        isRequired: false
      },
      email: {
        isVisible: true,
        isRequired: true,
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.email) && ctx.email !== '',
            message: theme.showRequiredText && 'E-mailadres is niet ingevuld'
          },
          {
            isValid: ctx => {
              if (_.isNil(ctx.email) || ctx.email === '') {
                return true;
              }

              return /^[-0-9a-zA-Z.+_]{1,63}@([-0-9a-zA-Z+_]+\.){1,63}[a-zA-Z]{1,63}$/.test(_.trim(ctx.email));
            },
            message: 'Er is geen geldig e-mailadres ingevuld.'
          }
        ]
      },
      gender: {
        isVisible: ctx => ctx.person.isGenderVisible === true,
        isRequired: ctx => ctx.person.isGenderRequired === true,
        rules: [
          {
            isValid: ctx => {
              if (ctx.person.isGenderRequired) {
                return !_.isNil(ctx.gender) && _.trim(ctx.gender) !== '';
              }

              return true;
            },
            message: theme.showRequiredText && 'Geslacht is niet ingevuld'
          }
        ]
      },
      initials: {
        isVisible: ctx => {
          return ctx.person.isInitialsVisible;
        },
        isRequired: ctx => {
          return ctx.person.isInitialsRequired;
        },
        rules: [
          {
            isValid: ctx => {
              if (ctx.person.isInitialsRequired) {
                return !_.isNil(ctx.initials) && _.trim(ctx.initials) !== '';
              }

              return true;
            },
            message: theme.showRequiredText && 'Voorletters is niet ingevuld'
          }
        ]
      },
      firstName: {
        isVisible: ctx => {
          return ctx.person.isFirstNameVisible;
        },
        isRequired: ctx => {
          return ctx.person.isFirstNameRequired;
        },
        rules: [
          {
            isValid: ctx => {
              if (ctx.person.isFirstNameRequired) {
                return !_.isNil(ctx.firstName) && _.trim(ctx.firstName) !== '';
              }

              return true;
            },
            message: theme.showRequiredText && 'Voornaam is niet ingevuld'
          },
          {
            isValid: ctx => {
              if (_.isNil(ctx.firstName) || ctx.firstName === '') {
                return true;
              }

              return _.trim(ctx.firstName).length > 1;
            },
            message: 'Voornaam moet minimaal 2 karakters hebben.'
          }
        ]
      },
      infix: {},
      lastName: {
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.lastName) && ctx.lastName !== '',
            message: theme.showRequiredText && 'Achternaam is niet ingevuld'
          },
          {
            isValid: ctx => {
              if (_.isNil(ctx.lastName) || ctx.lastName === '') {
                return true;
              }

              return _.trim(ctx.lastName).length > 1;
            },
            message: 'Achternaam moet minimaal 2 karakters hebben.'
          }
        ]
      },
      postalCode: {
        isVisible: isAddressVisible,
        isRequired: ctx => {
          if (hasValue(ctx.houseNumber_i) || hasValue(ctx.houseNumberAddition)) {
            return true;
          }

          return ctx.person.isAddressRequired;
        },
        rules: [
          {
            isValid: ctx => {
              if (!hasValue(ctx.postalCode)) {
                return true;
              }

              return /^[1-9][0-9]{3}\W?[a-zA-Z]{2}$/.test(_.trim(ctx.postalCode));
            },
            message: 'Er is geen geldige postcode ingevuld.'
          },
          {
            isValid: ctx => {
              if (hasValue(ctx.houseNumber_i) || hasValue(ctx.houseNumberAddition)) {
                return hasValue(ctx.postalCode);
              }

              if (ctx.person.isAddressRequired) {
                return hasValue(ctx.postalCode);
              }

              return true;
            },
            message: theme.showRequiredText && 'Postcode is niet ingevuld'
          },
          {
            message: 'Het opgegeven adres kan niet worden gevonden.',
            isValid: ctx => ctx.addressCleansingStatus !== 'RED'
          }
        ]
      },
      houseNumber_i: {
        isVisible: isAddressVisible,
        isRequired: ctx => {
          if (hasValue(ctx.postalCode) || hasValue(ctx.houseNumberAddition)) {
            return true;
          }

          return ctx.person.isAddressRequired;
        },
        rules: [
          {
            isValid: ctx => ctx.addressCleansingStatus !== 'RED'
          },
          {
            isValid: ctx => {
              if (!hasValue(ctx.houseNumber_i)) {
                return true;
              }

              return ctx.houseNumber_i.length < 8 && /^\d*$/.test(ctx.houseNumber_i);
            },
            message: 'Er is geen geldig huisnummer ingevuld. Controleer of er alleen cijfers gebruikt zijn.'
          },
          {
            isValid: ctx => {
              if (hasValue(ctx.postalCode) || hasValue(ctx.houseNumberAddition)) {
                return hasValue(ctx.houseNumber_i);
              }

              if (ctx.person.isAddressRequired) {
                return hasValue(ctx.houseNumber_i);
              }

              return !(ctx.addressCleansingStatus === 'RED' && !_.isNil(ctx.houseNumber_i));
            },
            message: theme.showRequiredText && 'Huisnummer is niet ingevuld'
          }
        ]
      },
      houseNumberAddition: {
        isVisible: isAddressVisible
      },
      telephone: {
        isVisible: ctx => {
          return ctx.person.isTelephoneVisible;
        },
        isRequired: ctx => {
          return ctx.person.isTelephoneRequired;
        },
        rules: [
          {
            isValid: ctx => {
              if (ctx.person.isTelephoneRequired) {
                return hasValue(ctx.telephone);
              }

              return true;
            },
            message: theme.showRequiredText && 'Telefoonnummer is niet ingevuld'
          },
          {
            isValid: ctx => {
              if (!hasValue(ctx.telephone)) {
                return true;
              }

              return formatPhone(ctx.telephone).result === 'GREEN';
            },
            message: 'Er is geen geldig telefoonnummer ingevuld.'
          }
        ]
      },
      dateOfBirth: {
        isVisible: ctx => ctx.person.isDateOfBirthVisible,
        isRequired: ctx => {
          return ctx.person.isDateOfBirthRequired;
        },
        rules: [
          {
            isValid: ctx => {
              if (ctx.person.isDateOfBirthRequired) {
                return hasValue(ctx.dateOfBirth);
              }

              return true;
            },
            message: theme.showRequiredText && 'Geboortedatum is niet ingevuld'
          }
        ]
      },
      street: {
        isVisible: ctx => ctx.addressCleansingStatus === 'GREEN' && isAddressVisible(ctx)
      },
      city: {
        isVisible: ctx => ctx.addressCleansingStatus === 'GREEN' && isAddressVisible(ctx)
      },
      noAddress: {
        isVisible: ctx => ctx.addressCleansingStatus !== 'GREEN' && isAddressVisible(ctx)
      },
      allowNewsletter: {
        isVisible: () => formContext.allowNewsletter === true
      },
      iban: {
        isVisible: true,
        // isRequired: ctx => ctx.paymentMethod === 'directDebit',
        isRequired: true,
        rules: [
          {
            isValid: ctx => {
              return !_.isNil(ctx.iban) && ctx.iban !== '';
            },
            message: theme.showRequiredText && 'Rekeningnummer is niet ingevuld'
          },
          {
            message: 'Vul hier een geldig IBAN nummer in.',
            isValid: ctx => {
              const value = ctx.iban;

              if (_.isNil(value) || value === '') {
                return true;
              }

              return IBAN.isValid(value);
            }
          },
          {
            message: 'IBAN moet afkomstig zijn uit een SEPA land',
            isValid: ctx => {
              const value = ctx.iban;
              if (_.isNil(value) || value === '') return true;

              return isSepaCountryIban(value);
            }
          }
        ]
      },
      donationReason: {
        isVisible: true,
        isRequired: true
      }
    }
  };

  const Email = () => (
    <div className='marginBottom'>
      <Field
        label='E-mailadres'
        name='email'
        inputComponent={Input}
        component={FieldWrapper}
        placeholder='E-mailadres'
        inputType='email'
        autoComplete='email'
      />
      <NewsletterOptIn label={parseText(formContext, formContext.newsletterTextMembership || 'Houd mij op de hoogte over nieuwe ontwikkelingen.')} />
    </div>
  );

  const AmountLabel = styled.div`
    font-size: ${props => props.theme?.membership?.amountLabel?.fontSize};
  `;

  const Amounts = () => formContext.amountMode !== 'dynamic' ? null : <div className='marginBottom'>
    <Field
      list='amounts'
      name='amountChoice'
      component={(props) => {
        const list = props.list.map(a => {
          a.fieldName = a.value.fieldName;
          a.value = a.value.value;
          return a;
        });

        return <>
          <div className='marginBottomTen'>
            <AmountLabel>{formContext.amountLabel}</AmountLabel>
          </div>
          <div className='amountButtons marginBottomTen'>
            <ButtonContainer
              fieldName='amountChoice'
              valueName='amountChoice'
              list={list} />
          </div>
        </>;
      }}
    />
    {formContext.customAmountDisplay === 'toggle' && <Field
      name='showCustomAmount'
      component={({setValue, value, updateField}) => value ? null :
        <div className='marginBottomTen centered'>
          <button type='button' className='link' onClick={() => {
            setValue(true);
            updateField('amountChoice', '');
          }}>
            Of kies een ander bedrag
          </button>
        </div>}
    />}
    <Field
      label={formContext.customAmountDisplay === 'inline' ? null : 'Vul het bedrag in dat je wilt doneren'}
      name='customAmount_m'
      placeholder={formContext.customAmountDisplay === 'inline' ? 'Of vul hier een ander bedrag in' : '0,00'}
      component={FieldWrapper}
      noDefault
      inputComponent={InputAmount}
    />
    {formContext.customAmountDisplay === 'toggle' && <Field
      name='showCustomAmount'
      component={({setValue, value}) => value ?
        <button type='button' className='link' onClick={() => setValue(false)}>Terug</button> : null}
    />}
    <Field
      name='amount_m'
      component={() => <SelectedAmountText formContext={formContext} amounts={formContext.membershipAmounts} className='center' />}
    />
  </div>;

  const PersonForm = props => {
    const blocks = {
      gender: <Gender genderDisplay={props.genderDisplay} />,
      name: <Name isFirstNameVisible={props.isFirstNameVisible}
        isFirstNameRequired={props.isFirstNameRequired}
        isInitialsRequired={props.isInitialsRequired} />,
      address: <Address {...props} />,
      phone: <Phone {...props} />,
      dateOfBirth: <DateOfBirth />,
      email: <Email />
    };
    return (
      <>
        {orderContactBlocks(formContext.person.fieldOrder, blocks).map(({key, node}) => <React.Fragment key={key}>{node}</React.Fragment>)}
        <Amounts />
        <Iban />
      </>
    );
  };

  let content = (
    <Box position={formContext.textPosition}
      hasTeaser={!_.isNil(formContext.pageTeaserHeader) || !_.isNil(formContext.pageTeaserBodyText) || !_.isEmpty(formContext.pageTeaserCarouselItems)}>
      <Title title={formContext.step1Header}>{theme.hideProgressBarMembership !== true && <ProgressBar progress={progressBar} />}</Title>
      <Form autoComplete='off' formInstanceKey='donate' formContext={formContext} formConfig={formConfig}>
        {!theme?.error?.showBelowBodyText && <FormValidation component={ErrorMessage} errorMessageTemplate={formContext.errorMessageTemplate} formContext={formContext} errorMessage='Je hebt nog niet alle velden ingevuld.' />}
        <div className='widgetContent'>
          {theme?.title?.showInBody && <TextToHtml text={formContext.step1Header} wrap={wrapTitle} />}
          <div>{parse(replaceNewLinesForHtml(parseText(formContext, formContext.step1BodyText)))}</div>
          {theme?.error?.showBelowBodyText && <FormValidation component={ErrorMessage} errorMessageTemplate={formContext.errorMessageTemplate} formContext={formContext} errorMessage='Je hebt nog niet alle velden ingevuld.' />}
          <Field
            name='renderAsPerson'
            telemarketingOptIn={formContext.telemarketingOptIn}
            telemarketingInfoText={parseText(formContext, formContext.telemarketingInfoText)}
            telemarketingCheckBoxText={parseText(formContext, formContext.telemarketingCheckBoxText)}
            isAddressVisible={formContext.person.isAddressVisible}
            isAddressOptional={!formContext.person.isAddressRequired}
            isFirstNameVisible={formContext.person.isFirstNameVisible}
            isFirstNameRequired={formContext.person.isFirstNameRequired}
            isInitialsRequired={formContext.person.isInitialsRequired}
            genderDisplay={formContext.genderDisplay}
            component={PersonForm}
          />

          <div className={'donationReason'} aria-hidden='true'>
            <Field
              name='donationReason'
              component={FieldWrapper}
              inputComponent={Input}
              label={'Reden donatie'}
              placeholder={'Reden donatie'}
              hideValidation
              tabIndex={'-1'}
              autoComplete={'e0e3896c-f25b-422d-8da1-bdc2466652d8'}
            />
          </div>

          <div className='marginBottom'>
            <FormValidation
              type='submit'
              label={parseText(formContext, formContext.membershipCallToActionLabel || 'Ja, ik word lid')}
              component={Button}
            />
          </div>
          {formContext.telemarketingOptIn === 'button' && (
            <Telemarketing
              telemarketingOptIn={formContext.telemarketingOptIn}
              telemarketingInfoText={parseText(formContext, formContext.telemarketingInfoText)}
              telemarketingCheckBoxText={parseText(formContext, formContext.telemarketingCheckBoxText)}
            />
          )}
          {formContext.privacyConfirmationTextMembership && (
            <div className='marginBottom'>
              <PrivacyLabel page='membership' text={parseAuthorizeLabel(formContext.privacyConfirmationTextMembership, renderDialog)} />
            </div>
          )}
          {formContext.entryCallToAction && (
            <div className='marginBottom centered'>
              {formContext.entryCallToActionUrl ? (
                <a className='link' href={formContext.entryCallToActionUrl} rel='noopener noreferrer'>
                  {formContext.entryCallToAction}
                </a>
              ) : (
                formContext.entryCallToAction
              )}
            </div>
          )}
          <BottomImage url={formContext.bottomImageUrl} altText={formContext.bottomImageAltText} />
        </div>
      </Form>
    </Box>
  );

  if (isDialogVisible) {
    content = (
      <ModalDialog
        close={() => {
          closeDialog();
        }}
      >
        <div dangerouslySetInnerHTML={{__html: formContext.privacyStatement}} />
      </ModalDialog>
    );
  }

  return (
    <>
      <Helmet>
        <title>{formContext.contactDataBrowserTitle}</title>
      </Helmet>
      <MainContainer
        backgroundImageUrl={formContext.backgroundImageUrl}
        backgroundImageTabletUrl={formContext.backgroundImageTabletUrl}
        backgroundImageMobileUrl={formContext.backgroundImageMobileUrl}
      />
      <LayoutContainer
        position={formContext.textPosition}
        header={
          <div>
            <div style={{float: 'left', display: 'inline-block'}}>
              <Logo url={formContext.websiteUrl} />
            </div>
          </div>
        }
        content={content}
        sidebar={
          <>
            {theme?.djsb?.showDJSB && <DJSBSideBar formContext={formContext} />}
            <Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} />
          </>
        }
        faq={formContext.frequentlyAskedQuestions}
        footer={formContext.footer}
        formContext={formContext}
      />
    </>
  );
};

const mapStateToProps = state => {
  return {
    formContext: state.reducer.formContext,
    isDialogVisible: state.reducer.isDialogVisible
  };
};

const mapDispatchToProps = dispatch => ({
  save: history => {
    dispatch(saveMembership(history));
  },
  gaMembership1: formContext => {
    dispatch(gaMembership1(formContext));
  },
  cleanse: (data, callback) => {
    dispatch(cleanseAddress(data, callback));
  },
  resetSubmittingForm: () => dispatch(resetSubmitting('donate')),
  renderDialog: e => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(renderDialog());
  },
  closeDialog: () => {
    dispatch(closeDialog());
  },
  untouchAmountFields: () => {
    dispatch(untouch({instanceKey: 'donate', field: 'customAmount_m'}));
    dispatch(untouch({instanceKey: 'donate', field: 'amount_m'}));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(Membership1)));

const Address = props => {
  if (!props.isAddressVisible) return <></>;
  return (
    <>
    <div className='marginBottom'>
      <StyledAddressLabel>
        <Label label='Adres' isOptional={props.isAddressOptional} />
      </StyledAddressLabel>
      <AddressRow>
        <div>
          <Field
            name='postalCode'
            component={() => <SubLabel label='Postcode' />}
            hideValidation
          />
          <Field
            name='postalCode'
            component={FieldWrapper}
            inputComponent={Input}
            placeholder={'Postcode'}
            inline
            hideValidation
            autoComplete='postal-code'
          />
        </div>
        <div>
          <HouseNumberRow>
            <Field
              name='houseNumber_i'
              component={() => <SubLabel label='Huisnummer' />}
              hideValidation
            />
            <Field
              name='houseNumberAddition'
              component={() => <SubLabel label='Toevoeging' isOptional />}
              hideValidation
            />
          </HouseNumberRow>
          <HouseNumberRow>
            <Field
              name='houseNumber_i'
              component={FieldWrapper}
              inputComponent={Input}
              placeholder={'Huisnummer'}
              inline
              hideValidation
              inputType='tel'
              autoComplete='05d2ae54-5b99-4418-9d38-fde0ab6d553f'
            />
            <Field
              name='houseNumberAddition'
              component={FieldWrapper}
              inputComponent={Input}
              placeholder={'Toevoeging'}
              hideValidation
              autoComplete='f1a99033-6af3-4aa2-94db-1de7ff1af61d'
            />
          </HouseNumberRow>
        </div>
      </AddressRow>
      <Field name='postalCode' component={Validation} />
      <Field name='houseNumber_i' component={Validation} />
      <Field name='houseNumberAddition' component={Validation} />
      <FoundAddress>
        <FoundAddressTitle>Adres:</FoundAddressTitle>
        <Field name='noAddress' component={() => <NoFoundAddress>Wordt automatisch aangevuld</NoFoundAddress>} />
        <Field component={({value}) => <div className='addressBox'>{value}&nbsp;</div>} name='street' />
        <Field component={({value}) => <div className='addressBox'>{value}</div>} name='city' />
      </FoundAddress>
    </div>
    </>
  );
};

const Phone = props => {
  return (
    <>
      <div className='marginBottom'>
        <Field
          label='Telefoonnummer'
          name='telephone'
          component={FieldWrapper}
          inputComponent={Input}
          placeholder='Telefoonnummer'
          inputType='tel'
          autoComplete='tel'
        />
        {(props.telemarketingOptIn === 'text' || props.telemarketingOptIn === 'checkbox' || props.telemarketingOptIn === 'dropdown') && <Telemarketing {...props} />}
      </div>
    </>
  );
};

const DateOfBirth = () => (
  <div className='marginBottom'>
    <Field
      label='Geboortedatum'
      name='dateOfBirth'
      component={FieldWrapper}
      inputComponent={DateInput}
      autoComplete='5d65176f-1708-4b09-93c4-9b9e4f97e4dc'
    />
  </div>
);

const Gender = ({genderDisplay}) => (
  <div className='marginBottom'>
    <Field
      label='Geslacht'
      name='gender'
      list='genders'
      component={FieldWrapper}
      inputComponent={props => {
        return genderDisplay === 'dropdown'
          ? <Dropdown fieldName='gender' valueName='gender' {...props} placeholder='Kiezen...' />
          : <RadioButtonContainer fieldName='gender' valueName='gender' list={props.list} {...props} />;
      }}
    />
  </div>
);

const NameRow = styled.div`
  display: flex;
  gap: ${props => props.theme.horizontalFieldGap || '5px'};

  > * {
    flex: 1;

    &:last-child {
      flex: ${props => props.theme.nameRow?.lastNameFlex || '3'};
    }
  }
`;

const Name = props => (
  <div className='marginBottom'>
    <Label label={'Naam'} />
    {props.isFirstNameVisible && (
      <div className='marginBottomTen'>
        <Field
          name='firstName'
          component={FieldWrapper}
          inputComponent={Input}
          placeholder={'Voornaam'}
          hideValidation
          autoComplete='given-name'
        />
      </div>
    )}
    <NameRow>
      <Field
        name='initials'
        component={() => <SubLabel label='Voorletters' isOptional={!props.isInitialsRequired} />}
        hideValidation
      />
      <Field
        name='infix'
        component={() => <SubLabel label='Tussenvoegsel' isOptional />}
        hideValidation
      />
      <Field
        name='lastName'
        component={() => <SubLabel label='Achternaam' />}
        hideValidation
      />
    </NameRow>
    <NameRow>
      <Field
        name='initials'
        component={FieldWrapper}
        inputComponent={Input}
        placeholder={'Voorletters'}
        hideValidation
        inline
        autoComplete='00db6d76-1330-4b71-89aa-72fe7b9f238a'
      />
      <Field
        name='infix'
        component={FieldWrapper}
        inputComponent={Input}
        placeholder={'Tussenvoegsel'}
        hideValidation
        autoComplete='90ab3315-3929-4643-b78d-9de3e727eadf'
      />
      <Field
        name='lastName'
        component={FieldWrapper}
        inputComponent={Input}
        placeholder={'Achternaam'}
        hideValidation
        autoComplete='family-name'
      />
    </NameRow>
    <Field name='firstName' component={Validation} />
    <Field name='initials' component={Validation} />
    <Field name='infix' component={Validation} />
    <Field name='lastName' component={Validation} />
  </div>
);

const Iban = () => (
  <div className='marginBottom'>
    <Field label='Rekeningnummer' name='iban' component={FieldWrapper} inputComponent={Input} placeholder='NL11ABNA012345678' forceUppercase />
    <MandateTextConnected />
  </div>
);

const MandateTextStyled = styled.div`
  font-size: ${props => props.theme?.membership?.mandateText?.fontSize};
  color: ${props => props.theme?.membership?.mandateText?.color};
  font-style: ${props => props.theme?.membership?.mandateText?.fontStyle};
`;
const MandateText = ({formContext, formData}) => <MandateTextStyled>
  {parse(replaceNewLinesForHtml(parseText({...formContext, ...formData}, formContext.membershipMandateText)))}
</MandateTextStyled>;

const mapStateToProps2 = (state) => {
  return {
    formContext: state.reducer.formContext,
    formData: getFormData(state, 'donate')
  };
};
const MandateTextConnected = connect(mapStateToProps2)(MandateText);
